/**
 * Formatters
 * 
 * Pure functions that format data for display in the UI.
 * All functions are defensive (handle null/undefined gracefully).
 * 
 * Benefits:
 * - Consistent formatting across the entire app
 * - Easy to test (pure functions)
 * - Easy to update (change once, applies everywhere)
 * - Handles edge cases (null, undefined, invalid data)
 */

import { format, formatDistanceToNow, isValid } from 'date-fns';

// ============================================================================
// DATE & TIME FORMATTERS
// ============================================================================

/**
 * Format a timestamp to a readable date and time
 * 
 * @param {number|string|Date} timestamp - Unix timestamp (ms), ISO string, or Date object
 * @param {string} formatString - date-fns format string (default: 'MMM dd, yyyy h:mm a')
 * @returns {string} Formatted date string or 'Invalid Date' if invalid
 * 
 * @example
 * formatDate(1703302407333) // "Dec 23, 2023 10:40 AM"
 * formatDate(1703302407333, 'yyyy-MM-dd') // "2023-12-23"
 */
export const formatDate = (timestamp, formatString = 'MMM dd, yyyy h:mm a') => {
  if (!timestamp) return '—'; // Em dash for empty values
  
  try {
    // Convert to Date object
    const date = typeof timestamp === 'number' 
      ? new Date(timestamp) 
      : new Date(timestamp);
    
    // Check if valid
    if (!isValid(date)) return 'Invalid Date';
    
    return format(date, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Format a timestamp to a short date (no time)
 * 
 * @param {number|string|Date} timestamp
 * @returns {string} Formatted date string
 * 
 * @example
 * formatDateShort(1703302407333) // "Dec 23, 2023"
 */
export const formatDateShort = (timestamp) => {
  return formatDate(timestamp, 'MMM dd, yyyy');
};

/**
 * Format a timestamp to time only
 * 
 * @param {number|string|Date} timestamp
 * @returns {string} Formatted time string
 * 
 * @example
 * formatTime(1703302407333) // "10:40 AM"
 */
export const formatTime = (timestamp) => {
  return formatDate(timestamp, 'h:mm a');
};

/**
 * Format a timestamp to relative time (e.g., "2 hours ago")
 * 
 * @param {number|string|Date} timestamp
 * @returns {string} Relative time string
 * 
 * @example
 * formatRelativeTime(Date.now() - 7200000) // "2 hours ago"
 */
export const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '—';
  
  try {
    const date = typeof timestamp === 'number' 
      ? new Date(timestamp) 
      : new Date(timestamp);
    
    if (!isValid(date)) return 'Invalid Date';
    
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'Invalid Date';
  }
};

// ============================================================================
// DURATION FORMATTERS
// ============================================================================

/**
 * Format duration in milliseconds to human-readable format
 * 
 * @param {number} durationMs - Duration in milliseconds
 * @param {boolean} verbose - If true, shows "1 minute 5 seconds", if false shows "1m 5s"
 * @returns {string} Formatted duration string
 * 
 * @example
 * formatDuration(65000) // "1m 5s"
 * formatDuration(65000, true) // "1 minute 5 seconds"
 * formatDuration(3661000) // "1h 1m 1s"
 */
export const formatDuration = (durationMs, verbose = false) => {
  if (durationMs === null || durationMs === undefined) return '—';
  if (durationMs === 0) return '0s';
  if (durationMs < 0) return 'Invalid';
  
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  const remainingMinutes = minutes % 60;
  const remainingSeconds = seconds % 60;
  
  const parts = [];
  
  if (hours > 0) {
    parts.push(verbose ? `${hours} hour${hours !== 1 ? 's' : ''}` : `${hours}h`);
  }
  
  if (remainingMinutes > 0) {
    parts.push(verbose ? `${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}` : `${remainingMinutes}m`);
  }
  
  if (remainingSeconds > 0 || parts.length === 0) {
    parts.push(verbose ? `${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}` : `${remainingSeconds}s`);
  }
  
  return parts.join(' ');
};

/**
 * Format duration to MM:SS format (like audio players)
 * 
 * @param {number} durationMs - Duration in milliseconds
 * @returns {string} Formatted duration string
 * 
 * @example
 * formatDurationMMSS(65000) // "1:05"
 * formatDurationMMSS(3661000) // "61:01"
 */
export const formatDurationMMSS = (durationMs) => {
  if (durationMs === null || durationMs === undefined) return '0:00';
  if (durationMs < 0) return '0:00';
  
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// ============================================================================
// PHONE NUMBER FORMATTER
// ============================================================================

/**
 * Format a phone number to a readable format
 * Handles US phone numbers (+1XXXXXXXXXX) and international numbers
 * 
 * @param {string} phoneNumber - Phone number (e.g., "+12137771234")
 * @returns {string} Formatted phone number
 * 
 * @example
 * formatPhoneNumber("+12137771234") // "(213) 777-1234"
 * formatPhoneNumber("+442071234567") // "+44 20 7123 4567"
 * formatPhoneNumber("12137771234") // "(213) 777-1234"
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '—';
  
  // Remove all non-digit characters except leading +
  const cleaned = phoneNumber.replace(/[^\d+]/g, '');
  
  // Check if it's a US number (+1 or starts with 1 and is 11 digits)
  const isUSNumber = cleaned.startsWith('+1') || 
                     (cleaned.startsWith('1') && cleaned.length === 11);
  
  if (isUSNumber) {
    // Extract the 10-digit number (remove country code)
    const digits = cleaned.replace(/^\+?1/, '');
    
    // Format as (XXX) XXX-XXXX
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
  }
  
  // For international numbers, just add spaces for readability
  // Format: +XX XX XXXX XXXX
  if (cleaned.startsWith('+')) {
    const countryCode = cleaned.slice(0, 3); // +XX
    const rest = cleaned.slice(3);
    
    // Add space every 4 digits
    const formatted = rest.match(/.{1,4}/g)?.join(' ') || rest;
    return `${countryCode} ${formatted}`;
  }
  
  // Return as-is if we can't format it
  return phoneNumber;
};

// ============================================================================
// CURRENCY FORMATTER
// ============================================================================

/**
 * Format cents to dollar amount
 * 
 * @param {number} cents - Amount in cents
 * @returns {string} Formatted currency string
 * 
 * @example
 * formatCurrency(70) // "$0.70"
 * formatCurrency(1599) // "$15.99"
 * formatCurrency(0) // "$0.00"
 */
export const formatCurrency = (cents) => {
  if (cents === null || cents === undefined) return '—';
  if (typeof cents !== 'number') return '—';
  
  const dollars = cents / 100;
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
};

// ============================================================================
// LATENCY FORMATTER
// ============================================================================

/**
 * Format latency in milliseconds
 * 
 * @param {number} latencyMs - Latency in milliseconds
 * @returns {string} Formatted latency string
 * 
 * @example
 * formatLatency(800) // "800ms"
 * formatLatency(1500) // "1.5s"
 * formatLatency(null) // "—"
 */
export const formatLatency = (latencyMs) => {
  if (latencyMs === null || latencyMs === undefined) return '—';
  if (typeof latencyMs !== 'number') return '—';
  
  // If less than 1 second, show in milliseconds
  if (latencyMs < 1000) {
    return `${Math.round(latencyMs)}ms`;
  }
  
  // If 1 second or more, show in seconds with 1 decimal place
  const seconds = latencyMs / 1000;
  return `${seconds.toFixed(1)}s`;
};

// ============================================================================
// TEXT FORMATTERS
// ============================================================================

/**
 * Truncate text to a maximum length and add ellipsis
 * 
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text with ellipsis if needed
 * 
 * @example
 * truncateText("This is a very long text", 10) // "This is a..."
 * truncateText("Short", 10) // "Short"
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  
  return text.slice(0, maxLength) + '...';
};

/**
 * Capitalize first letter of a string
 * 
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 * 
 * @example
 * capitalize("hello world") // "Hello world"
 */
export const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Convert snake_case to Title Case
 * 
 * @param {string} str - Snake case string
 * @returns {string} Title case string
 * 
 * @example
 * snakeToTitle("user_hangup") // "User Hangup"
 * snakeToTitle("error_llm_websocket") // "Error Llm Websocket"
 */
export const snakeToTitle = (str) => {
  if (!str) return '';
  
  return str
    .split('_')
    .map(word => capitalize(word))
    .join(' ');
};

// ============================================================================
// NUMBER FORMATTERS
// ============================================================================

/**
 * Format a number with commas for thousands
 * 
 * @param {number} num - Number to format
 * @returns {string} Formatted number string
 * 
 * @example
 * formatNumber(1234567) // "1,234,567"
 * formatNumber(42) // "42"
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined) return '—';
  if (typeof num !== 'number') return '—';
  
  return new Intl.NumberFormat('en-US').format(num);
};

/**
 * Format a percentage
 * 
 * @param {number} value - Decimal value (0.75 = 75%)
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted percentage string
 * 
 * @example
 * formatPercentage(0.75) // "75%"
 * formatPercentage(0.8567, 2) // "85.67%"
 */
export const formatPercentage = (value, decimals = 0) => {
  if (value === null || value === undefined) return '—';
  if (typeof value !== 'number') return '—';
  
  return `${(value * 100).toFixed(decimals)}%`;
};

